import { Helmet } from 'react-helmet-async';
import { SEOMetadata, generateSEOMetadata, PAGE_TYPES } from '@/types/seo';
import { JSONLDGenerator, useJSONLD } from '@/utils/jsonLd';
import { useBrand } from '@/config/brands';
import { useEffect } from 'react';

interface SEOHeadProps {
  pageType: string;
  dynamicData?: Record<string, any>;
  customMetadata?: Partial<SEOMetadata>;
  jsonLdData?: any;
  children?: React.ReactNode;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  pageType,
  dynamicData = {},
  customMetadata = {},
  jsonLdData,
  children
}) => {
  const brand = useBrand();

  // Generate SEO metadata from brand-aware templates
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const baseMetadata = generateSEOMetadata(pageType, dynamicData, hostname);
  const metadata = { ...baseMetadata, ...customMetadata };

  // Generate JSON-LD schemas
  const jsonLdSchemas = useJSONLD(pageType, jsonLdData || dynamicData);

  // Log SEO data in development for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.group(`SEO Debug - ${pageType}`);
      console.log('Metadata:', metadata);
      console.log('JSON-LD Schemas:', jsonLdSchemas);
      console.groupEnd();
    }
  }, [pageType, metadata, jsonLdSchemas]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="keywords" content={metadata.keywords.join(', ')} />
      <meta name="robots" content={metadata.robotsMeta} />
      <link rel="canonical" href={metadata.canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={metadata.ogTitle} />
      <meta property="og:description" content={metadata.ogDescription} />
      <meta property="og:image" content={metadata.ogImage} />
      <meta property="og:type" content={metadata.ogType} />
      <meta property="og:url" content={metadata.canonicalUrl} />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content={metadata.twitterCard} />
      <meta name="twitter:title" content={metadata.twitterTitle} />
      <meta name="twitter:description" content={metadata.twitterDescription} />
      <meta name="twitter:image" content={metadata.twitterImage} />

      {/* JSON-LD Schema Markup */}
      {jsonLdSchemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schema.content }}
        />
      ))}

      {/* Additional custom head elements */}
      {children}
    </Helmet>
  );
};

// Specialized components for common page types
export const HomeSEO: React.FC<{ customMetadata?: Partial<SEOMetadata> }> = ({ customMetadata }) => {
  const brand = useBrand();
  return (
    <SEOHead
      pageType={PAGE_TYPES.HOME}
      customMetadata={{
        title: brand.seo.title,
        description: brand.seo.description,
        keywords: brand.seo.keywords,
        ogTitle: brand.seo.title,
        ogDescription: brand.seo.description,
        ogImage: brand.social.ogImage,
        twitterTitle: brand.seo.title,
        twitterDescription: brand.seo.description,
        twitterImage: brand.social.ogImage,
        ...customMetadata
      }}
    />
  );
};

export const HospitalSEO: React.FC<{
  hospital: {
    id: string;
    name: string;
    city: string;
    state: string;
    address?: string;
    phone?: string;
    website?: string;
    specialties?: string[];
    priceRange?: string;
    services?: Array<{
      name: string;
      type: string;
      description?: string;
      averagePrice?: number;
    }>;
  };
  customMetadata?: Partial<SEOMetadata>;
}> = ({ hospital, customMetadata }) => (
  <SEOHead
    pageType={PAGE_TYPES.HOSPITAL_PROFILE}
    dynamicData={{
      hospitalId: hospital.id,
      hospitalName: hospital.name,
      city: hospital.city,
      state: hospital.state,
      priceRange: hospital.priceRange || '$1,000-$50,000'
    }}
    jsonLdData={hospital}
    customMetadata={customMetadata}
  />
);

export const ProcedureSEO: React.FC<{
  procedure: {
    code: string;
    codingSystem: 'CPT' | 'DRG' | 'ICD-10' | 'HCPCS' | 'Revenue' | 'NDC';
    name: string;
    description?: string;
    alternateNames?: string[];
    bodyLocation?: string;
    procedureType?: string;
    priceRange?: string;
    averagePrice?: number;
    priceVariation?: {
      min: number;
      max: number;
      median: number;
    };
    priceComparison?: {
      procedureName: string;
      procedureCode: string;
      hospitals: Array<{
        hospitalId: string;
        hospitalName: string;
        price: number;
        city: string;
        state: string;
      }>;
    };
  };
  pageType: string;
  customMetadata?: Partial<SEOMetadata>;
}> = ({ procedure, pageType, customMetadata }) => {
  const dynamicData = {
    [procedure.codingSystem.toLowerCase() + 'Code']: procedure.code,
    procedureName: procedure.name,
    priceRange: procedure.priceRange || `$${procedure.priceVariation?.min || 0}-$${procedure.priceVariation?.max || 0}`
  };

  return (
    <SEOHead
      pageType={pageType}
      dynamicData={dynamicData}
      jsonLdData={procedure}
      customMetadata={customMetadata}
    />
  );
};

export const HospitalRankingsSEO: React.FC<{
  rankings: {
    title: string;
    description: string;
    datePublished: string;
    rankings: Array<{
      hospitalId: string;
      hospitalName: string;
      rank: number;
      score?: number;
      category: string;
    }>;
  };
  customMetadata?: Partial<SEOMetadata>;
}> = ({ rankings, customMetadata }) => (
  <SEOHead
    pageType={PAGE_TYPES.HOSPITAL_RANKINGS}
    jsonLdData={rankings}
    customMetadata={customMetadata}
  />
);

// Hook for pages that need to set SEO data programmatically
export const useSEO = (
  pageType: string,
  dynamicData: Record<string, any> = {},
  customMetadata: Partial<SEOMetadata> = {}
) => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const baseMetadata = generateSEOMetadata(pageType, dynamicData, hostname);
  const metadata = { ...baseMetadata, ...customMetadata };
  const jsonLdSchemas = useJSONLD(pageType, dynamicData);

  return {
    metadata,
    jsonLdSchemas,
    setMeta: (key: keyof SEOMetadata, value: any) => ({
      ...metadata,
      [key]: value
    })
  };
};

// Development helper component to visualize SEO data
export const SEODebugger: React.FC<{
  pageType: string;
  dynamicData?: Record<string, any>;
  show?: boolean;
}> = ({ pageType, dynamicData = {}, show = process.env.NODE_ENV === 'development' }) => {
  if (!show) return null;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const metadata = generateSEOMetadata(pageType, dynamicData, hostname);
  const jsonLdSchemas = useJSONLD(pageType, dynamicData);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '300px',
        maxHeight: '100vh',
        overflow: 'auto',
        backgroundColor: 'rgba(0,0,0,0.9)',
        color: 'white',
        padding: '10px',
        fontSize: '12px',
        zIndex: 9999,
        fontFamily: 'monospace'
      }}
    >
      <h4>SEO Debug: {pageType}</h4>
      <details>
        <summary>Metadata</summary>
        <pre>{JSON.stringify(metadata, null, 2)}</pre>
      </details>
      <details>
        <summary>JSON-LD ({jsonLdSchemas.length})</summary>
        {jsonLdSchemas.map((schema, index) => (
          <details key={index}>
            <summary>Schema {index + 1}</summary>
            <pre>{schema.content}</pre>
          </details>
        ))}
      </details>
    </div>
  );
};

export default SEOHead;